import React from 'react';
import { AIRecommendation } from '../types';
import { CheckCircleIcon, BookOpenIcon, AcademicCapIcon, LightBulbIcon } from '@heroicons/react/24/outline';

interface AIRecommendationsProps {
  recommendations: AIRecommendation[];
  onMarkAsRead: (id: string) => void;
  onViewRecommendation: (recommendation: AIRecommendation) => void;
}

const AIRecommendations: React.FC<AIRecommendationsProps> = ({
  recommendations,
  onMarkAsRead,
  onViewRecommendation
}) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'course':
        return <BookOpenIcon className="w-6 h-6 text-blue-600" />;
      case 'book':
        return <AcademicCapIcon className="w-6 h-6 text-green-600" />;
      case 'practice':
        return <LightBulbIcon className="w-6 h-6 text-yellow-600" />;
      default:
        return <LightBulbIcon className="w-6 h-6 text-purple-600" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (recommendations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <LightBulbIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-lg font-medium">No recommendations yet</p>
          <p className="text-sm">Complete more courses and assessments to get personalized recommendations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">AI Learning Recommendations</h3>
        <span className="text-sm text-gray-500">
          {recommendations.filter(r => !r.isRead).length} new
        </span>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {recommendations.map((recommendation) => (
          <div
            key={recommendation.id}
            className={`bg-white rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md ${
              recommendation.isRead ? 'border-gray-200 opacity-75' : 'border-blue-200'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                {getIcon(recommendation.type)}
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${getConfidenceColor(recommendation.confidence)}`}>
                    {Math.round(recommendation.confidence * 100)}% match
                  </span>
                  {!recommendation.isRead && (
                    <button
                      onClick={() => onMarkAsRead(recommendation.id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <CheckCircleIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              
              <h4 className="font-medium text-gray-900 mb-2">{recommendation.title}</h4>
              <p className="text-sm text-gray-600 mb-3">{recommendation.description}</p>
              
              <div className="mb-3">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {recommendation.type}
                </span>
              </div>
              
              <p className="text-xs text-gray-500 mb-4">
                <strong>Why:</strong> {recommendation.reason}
              </p>
              
              <button
                onClick={() => onViewRecommendation(recommendation)}
                className="w-full bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIRecommendations;
